import i18n, { TFunction } from 'i18next';
import HttpApi from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import intervalPlural from 'i18next-intervalplural-postprocessor';

import {
  LANGUAGES_WO_AR,
  Instance,
  Languages,
  LANGUAGES_UAE,
  LANGUAGES_WO_AR_RU,
  lookupLocalStorage,
  NS,
} from '@const/consts';
import * as Sentry from '@sentry/browser';
import { SeverityLevel } from '@sentry/types';

export const getInstanceWoState = (): string => {
  return window.location.hostname.match(/com.br|ilsbrasil.com/g)?.[0]
    ? Instance.BR
    : window.location.hostname.match(/ilsglobal.vn/g)
      ? Instance.VN
      : window.location.hostname.match(/.ae/g)
        ? Instance.UAE
        : (window.location.hostname.match(/global|ru|africa/g)?.[0] ??
          window.localStorage.getItem(lookupLocalStorage.INSTANCE) ??
          Instance.GLOBAL);
};

const instance = getInstanceWoState();

window.localStorage.setItem(lookupLocalStorage.INSTANCE, instance);

const isRussia = instance === Instance.RU;
const isUAE = instance === Instance.UAE;
const defaultLanguage = isRussia ? Languages.RU : Languages.EN;
const lngFromLS = window.localStorage.getItem(lookupLocalStorage.LANGUAGE) ?? defaultLanguage;

const setHtmlDirection = (language: string): void => {
  const isArabic = language === Languages.AR;
  const html = document.documentElement;

  html.lang = language;
  html.dir = isArabic ? 'rtl' : 'ltr';
  html.setAttribute('rtl', String(isArabic));
};

let translates: string[] = LANGUAGES_WO_AR_RU;

if (isRussia) {
  translates = LANGUAGES_WO_AR;
}

if (isUAE) {
  translates = LANGUAGES_UAE;
}

const finalLanguage = translates.includes(lngFromLS) ? lngFromLS : defaultLanguage;

i18n
  .use(HttpApi)
  .use(initReactI18next)
  .use(intervalPlural)
  .init({
    debug: false,
    fallbackLng: translates,
    lng: finalLanguage,
    ns: NS,
    preload: translates,
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    react: {
      nsMode: 'default',
    },

    interpolation: {
      escapeValue: false,
    },
  })
  .catch((err: TFunction) => {
    const errorText = 'i18n error';
    const errorLevel: SeverityLevel = 'fatal';
    const errorInfo = { err };

    Sentry.withScope((scope: Sentry.Scope) => {
      scope.setLevel(errorLevel);
      scope.setExtras(errorInfo);
      Sentry.captureException(new Error(errorText));
    });
  });

i18n.on('languageChanged', setHtmlDirection);
setHtmlDirection(i18n.language || finalLanguage);

export default i18n;
